import React, { useEffect, useState } from "react";
import {
  FaWhatsapp, FaEnvelope, FaCrown,
  FaProjectDiagram, FaUsers,
} from "react-icons/fa";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import MessageForm from "./MessageForm";
import { useSubscription } from "../context/SubscriptionContext";
import { apiGet } from "../utils/api";
import api from "../utils/axiosInstance";
import {
  Card, CardHeader, CardBody,
  StatCard, PageHeader, PageLoading, EmptyState,
  Alert,
} from "./ui";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

/* ── stage helpers ── */
const LEAD_STAGES = ["New", "Contacted", "Qualified", "Converted", "Lost"];
const STAGE_COLORS = {
  New: "rgba(1,113,222,0.86)",
  Contacted: "rgba(91,161,238,0.86)",
  Qualified: "rgba(1,85,168,0.86)",
  Converted: "rgba(5,150,105,0.82)",
  Lost: "rgba(220,38,38,0.78)",
};
const STAGE_BORDERS = {
  New: "rgb(1,113,222)",
  Contacted: "rgb(91,161,238)",
  Qualified: "rgb(1,85,168)",
  Converted: "rgb(5,150,105)",
  Lost: "rgb(220,38,38)",
};

function countByStage(leads) {
  const counts = {};
  LEAD_STAGES.forEach((s) => (counts[s] = 0));
  (leads || []).forEach((l) => {
    const stage = l.stage || "New";
    const key = LEAD_STAGES.includes(stage) ? stage : "New";
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

export default function Dashboard() {
  const { subscription, loading } = useSubscription();

  const [flows, setFlows] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [connectedSessions, setConnectedSessions] = useState(0);
  const [extraLoading, setExtraLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      apiGet("/api/autoflows").then((res) => setFlows(res?.data || [])),
      apiGet("/api/contacts?isLead=true&limit=5000").then((res) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.contacts) ? res.contacts : Array.isArray(res?.leads) ? res.leads : Array.isArray(res?.data) ? res.data : [];
        setLeads(list);
        // Total comes from the API count, not the (capped) returned page —
        // otherwise large lead pools wrongly display as exactly 5000.
        setLeadTotal(typeof res?.total === "number" ? res.total : list.length);
      }),
      api.get("/api/sessions").then((res) => {
        const all = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setConnectedSessions(all.filter(s => s.status === 'connected').length);
      }),
    ]).finally(() => setExtraLoading(false));
  }, []);

  if (loading) return <PageLoading />;
  if (!subscription) return <EmptyState title="No subscription found" />;

  /* ── subscription data ── */
  const planName = subscription.plan?.name || "Free";
  const expiryDate = subscription.subscription?.interval === 'lifetime'
    ? "Lifetime Access"
    : subscription.subscription?.currentPeriodEnd
      ? new Date(subscription.subscription.currentPeriodEnd).toLocaleDateString()
      : "N/A";
  const status = subscription.subscription?.status || "unknown";
  const isExpired = subscription.status === "expired";

  const maxConnections = subscription?.usageByKey?.whatsappConnection?.total ?? 0;
  const usedConnections = connectedSessions || subscription?.usageByKey?.whatsappConnection?.used || 0;

  const usedMessages = subscription?.usageByKey?.message?.used ?? 0;
  const maxMessages = subscription?.usageByKey?.message?.total ?? 0;
  const remainingMessages = Math.max(maxMessages - usedMessages, 0);

  const activeFlows = flows.filter((f) => f.active || f.status === "active").length;
  const totalLeads = leadTotal;
  const stageCounts = countByStage(leads);

  const statusColor =
    status === "active"
      ? "bg-brand"
      : status === "expired"
      ? "bg-red-500"
      : "bg-yellow-500";

  /* ── chart data ── */
  const messagesChartData = {
    labels: ["Sent", "Remaining"],
    datasets: [
      {
        data: [usedMessages, remainingMessages],
        backgroundColor: ["rgba(1,113,222,0.88)", "rgba(203,213,225,0.55)"],
        borderColor: ["rgb(1,113,222)", "rgb(203,213,225)"],
        borderWidth: 1,
        hoverOffset: 6,
      },
    ],
  };

  const messagesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { padding: 16, usePointStyle: true, pointStyleWidth: 10, color: "#64748B", font: { family: "Inter", size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed.toLocaleString()}`,
        },
      },
    },
    cutout: "60%",
  };

  const leadsChartData = {
    labels: LEAD_STAGES,
    datasets: [
      {
        label: "Leads",
        data: LEAD_STAGES.map((s) => stageCounts[s] || 0),
        backgroundColor: LEAD_STAGES.map((s) => STAGE_COLORS[s]),
        borderColor: LEAD_STAGES.map((s) => STAGE_BORDERS[s]),
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 48,
      },
    ],
  };

  const leadsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y} lead${ctx.parsed.y !== 1 ? "s" : ""}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#64748B", font: { family: "Inter", size: 11 } },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, color: "#64748B", font: { family: "Inter", size: 11 } },
        grid: { color: "rgba(148,163,184,0.18)" },
      },
    },
  };

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Monitor messaging capacity, active automations, connected numbers, and customer activity." />

      {isExpired && (
        <Alert variant="danger" className="mb-6">
          Your plan has expired. Please renew to continue using all features.
        </Alert>
      )}

      {/* ── Stat cards ── */}
      <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={FaEnvelope}
          label="Messages Sent"
          value={usedMessages.toLocaleString()}
          sub={`of ${maxMessages.toLocaleString()} quota`}
          progress={usedMessages}
          progressMax={maxMessages}
          progressVariant="info"
        />
        <StatCard
          icon={FaProjectDiagram}
          label="Active Flows"
          value={extraLoading ? "..." : activeFlows}
          sub={`${flows.length} total flow${flows.length !== 1 ? "s" : ""}`}
        />
        <StatCard
          icon={FaWhatsapp}
          label="WhatsApp Numbers"
          value={`${usedConnections} / ${maxConnections}`}
          sub="Connected numbers"
          progress={usedConnections}
          progressMax={maxConnections}
        />
        <StatCard
          icon={FaUsers}
          label="Leads"
          value={extraLoading ? "..." : totalLeads.toLocaleString()}
          sub="Total captured leads"
        />
      </div>

      {/* ── Charts ── */}
      <div className="mb-7 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>Messages Overview</CardHeader>
          <CardBody>
            <div className="flex items-center justify-center" style={{ height: 280 }}>
              {maxMessages > 0 ? (
                <Doughnut data={messagesChartData} options={messagesChartOptions} />
              ) : (
                <p className="text-sm text-ink-muted">No message quota data available.</p>
              )}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>Leads by Stage</CardHeader>
          <CardBody>
            <div style={{ height: 280 }}>
              {totalLeads > 0 ? (
                <Bar data={leadsChartData} options={leadsChartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-ink-muted">No leads data yet.</p>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* ── Quick send + plan info ── */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card className="min-h-[400px]">
          <CardHeader>Send Message</CardHeader>
          <CardBody className="flex-1">
            <MessageForm />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>Plan Details</CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-100"
                    style={{ background: "rgba(1,113,222,0.1)" }}
                  >
                    <FaCrown size={15} color="var(--color-brand)" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-ink-primary">{planName}</p>
                    <p className="text-xs text-ink-muted">Current plan</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${statusColor} inline-block`} />
                  <span className="text-xs text-ink-muted capitalize">{status}</span>
                </div>
              </div>

              <div className="border-t border-surface-border pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-ink-muted">Renewal</span>
                  <span className="text-ink-primary font-medium">{expiryDate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ink-muted">Messages used</span>
                  <span className="text-ink-primary font-medium">
                    {usedMessages.toLocaleString()} / {maxMessages.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ink-muted">WhatsApp numbers</span>
                  <span className="text-ink-primary font-medium">
                    {usedConnections} / {maxConnections}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ink-muted">Active flows</span>
                  <span className="text-ink-primary font-medium">
                    {extraLoading ? "..." : activeFlows}
                  </span>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
